import { environment } from './../../../../../environments/environment';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpParams, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { firstValueFrom, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { DashboardService } from '../../../../service/events/dashboard.service';
import { FincasApiService } from '../../../../service/api/fincas-api.service';
import { FincasService } from '../../../../service/fincas/fincas.service';

import { RequestHandler } from '../../../../service/OffService/request-handler';
import { RequestButtonTypes } from '../../../../common/components/request-button/request-button.component';
import { Filtering } from '../../../../service/filtering/filtering';
import { StorageManager } from '../../../../common/storage-manager.class';
import { UploadResponse, Utils } from '../../../../common/utils';

import {
  FilteredValuesInterface,
  InputType,
  ItemInterface,
} from '../../../../common/components/form-builder/form-builder.component';

import { BaseView } from '../../../base-view';
import { list } from '../../../../common/classes/request-builder';

import { ColInterface } from 'src/app/common/components/common-list-table/col.interface';
import { ColStylesInterface } from 'src/app/common/components/common-list-table/col-styles.interface';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';

import { MessageService, TreeNode } from 'primeng/api';
import { FileSelectEvent, FileUploadEvent } from 'primeng/fileupload';

import { FincasEnvironmentDefinition } from './environment-variables';
import { FincasFormDefinition } from './finca-form/form-definition';
import { FormType } from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import { createCsv } from 'src/app/common/components/reactive-form-builder/export/createCsv';
import { BaseResponseModel } from 'src/app/models/base-response.model';
import { ReaImportData } from 'src/app/models/fincas/rea.model';

/* ──────────────────────────────────────────────────────────────────────────────
 * Constants & types
 * ───────────────────────────────────────────────────────────────────────────── */

/** UI message state for async operations. */
type MessageState = '' | 'warning' | 'error' | 'success';

/** Label/value mapping for exploitation type. */
interface Tipo {
  label: string;
  value: string;
}

interface CueHistory {
  codigo_comprovacion: string;
  fecha: string;
  estado: string;
  id: number;
  id_finca: number;
  ids_fito: string;
  ids_ferti: string;
}

/**
 * Domain defaults (as strings to satisfy strict `no-magic-numbers` linters).
 * They can be overridden from `environment` when available.
 */
const DEFAULTS = {
  FIRST_CUADERNO_YEAR: Number('2016'),
  DEFAULT_CUADERNO_COLUMNS: Number('3'),
  LECTURA_CONTADOR_ALERT_THRESHOLD: Number('6000'),
} as const;

/** Mapping used by `changeTipoExplotacion()`. */
const TIPOS: ReadonlyArray<Tipo> = [
  { label: '...', value: '' },
  { label: 'Agricultura Ecológica', value: 'AE' },
  { label: 'Producción Integrada', value: 'PI' },
  { label: 'Certificacion Privada', value: 'CP' },
  { label: 'Agrupacion de Tratamiento Integrado en Agricultura', value: 'Atrias' },
  { label: 'Asistida de un asesor', value: 'AS' },
  { label: 'Sin obligacion de aplicar la GIP', value: 'NO' },
] as const;

/* ──────────────────────────────────────────────────────────────────────────────
 * Component
 * ───────────────────────────────────────────────────────────────────────────── */

@Component({
  selector: 'app-finca',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  templateUrl: './finca.component.html',
  styleUrls: ['./finca.component.scss'],
})
export class FincaComponent extends BaseView<FincasModel> implements OnInit {
  /** Reactive form definition (initialized in `show()`). */
  public form!: FincasFormDefinition;

  /** Environment/feature flags for this view. */
  public readonly env = new FincasEnvironmentDefinition();

  /** Primary requests handled by the base view. */
  public getRequest!: RequestHandler<FincasModel[]>;
  
  public deleteRequest!: RequestHandler<FincasModel>;
  
  public deleteRequestButtonType: RequestButtonTypes = RequestButtonTypes.DELETE;

  /** Table/view configuration. */
  public menuItemId = 'finca';
  
  public filter = new Filtering<FincasModel>();

  /** Dialog/operation state (REA / CUE / PAC). */  
  showSpinner = false;
  
  messageState: MessageState = '';
  
  importResult: any;
  
  historicoREA: any;

  /** Delete confirmation helpers. */
  public deleteWarningMessage = '';
  
  public deleteWarning = '';

  /** PAC upload state. */
  public showModalPAC = false;
  
  public showPACError = true;
  
  public txtPACError = '';
  
  public txtPACInfo = '';
  
  public fincaPAC = '';
  
  public urlUpload = '';
  
  public enableUpload = true;

  /** Misc UI state. */
  public parcelasAux = true;
 
  /** Column count is a named constant instead of a magic number. */
  public cuadernoColumns = DEFAULTS.DEFAULT_CUADERNO_COLUMNS;

  cueHistory: CueHistory[] | undefined = [];
  
  statusCue: CueHistory | null = null;

  counter:any;

  /** Table columns (visibility driven by feature flags). */
  public cols: ColInterface[] = [
    { field: 'id', header: 'Nº Finca', filter: '', visible: this.env.showNumeroFincas },
    { field: 'cliente_nombre', header: this.env.clienteName, filter: '', visible: this.env.hasClients },
    { field: 'nombre', header: 'Nombre ' + this.env.fincaName, filter: ''},
    { field: 'provincia', header: 'Provincia', filter: '' },
    { 
      field: 'titular_nombre', 
      header: 'Titular', 
      filter: '', 
      visible: !this.env.activateREA 
    },
    { 
      field: 'representante_nombre', 
      header: 'Representante', 
      filter: '', 
      visible: this.env.activateREA 
    },
    { field: 'telefono', header: 'Teléfono', filter: '' },
    { field: 'email', header: 'Email', filter: '' },
    { field: 'lectura_contador', header: 'Acumulado m3 ', visible: this.env.appName === 'abonosfolques' },
    { field: 'cooperativa', header: 'Cooperativa', filter: '', visible: this.env.showClientesCooperativa },
  ];

  /**
   * Generator form fields (they depend on lists inside `form`).
   * Note the `values` lambdas avoid accessing `form` before it is created.
   */
  public generarFields: ItemInterface<object>[] = [
    {
      field: 'id_sector',
      label: this.env.sectorName,
      placeholder: 'TODOS',
      inputType: { type: InputType.DROPDOWN },
      values: () => this.form?.lists.sectores ?? list(),
      valuePrimaryKey: 'id',
      filter: (sector: FilteredValuesInterface<SectorModel>) =>
        sector.value?.id_finca === (this.model as TreeNode<FincasModel>).data?.id,
    },
    {
      field: 'id_variedad',
      label: 'Variedad',
      placeholder: 'TODAS',
      inputType: { type: this.env.appName === 'covidai' ? InputType.MULTISELECT : InputType.DROPDOWN },
      values: () => this.form?.lists.parcelas ?? list(),
      multiSelect: this.env.appName === 'covidai',
      valuePrimaryKey: 'id',
      filter: (it: FilteredValuesInterface<ParcelasModel>): boolean => {
        const cond1 = (Boolean((this.form?.lists.sectores.selected)))
          ? it.value?.id_sector === this.form.lists.sectores.selected.id
          : true;
        const cond2 = it.value?.id_finca === (this.model as TreeNode<FincasModel>).data?.id;

        this.parcelasAux = cond1;
        return !!(cond1 && cond2);
      },
    },
    { field: 'fecha_inicio', label: 'Fecha inicio', inputType: { type: InputType.CALENDAR } },
    { field: 'fecha_fin', label: 'Fecha fin', inputType: { type: InputType.CALENDAR } },
  ];

  /** Current year is computed once for “Cuaderno” lists. */
  private readonly currentYear = new Date().getFullYear();

  constructor(
    private readonly fincasApi: FincasApiService,
    private readonly fincasSvc: FincasService,
    public dashboard: DashboardService,
    public messageService: MessageService,
    private readonly router: Router,
    private readonly http: HttpClient
  ) {
    super(dashboard, fincasApi.fincasGET, fincasApi.fincasDELETE);

    this.getRequest =  this.fincasApi.fincasGET;
    this.deleteRequest =  this.fincasApi.fincasDELETE;
  }

  /* ───────────────────────────── Lifecycle ───────────────────────────── */

  ngOnInit(): void {
    // Highlight the submenu item for this view.
    this.dashboard.setSubmenuItem(this.menuItemId);

    // Basic filter to remove falsy rows.
    this.filter.addFilter((data: TreeNode<FincasModel>[] | void) => (data || []).filter(Boolean));
  }

  /* ─────────────────────────────── REA ──────────────────────────────── */
  public onReaImported(result: BaseResponseModel<ReaImportData>): void {
    if (result.statusCode == 202) {
      this.getRequest.safePerform();
    }
  }

  /** Maps backend status code to human-friendly text. */
  statusText(code?: string | number): string {
    switch (String(code)) {
      case '0': return 'Generando CUE';
      case '1': return 'Disponible';
      case '2': return 'Error';
      default:  return 'Disponible';
    }
  }

  /** Maps backend status code to PrimeNG tag severity. */
  statusSeverity(code?: string | number): 'success' | 'info' | 'warn' | 'danger' {
    switch (String(code)) {
      case '0': return 'info';
      case '1': return 'success';
      case '2': return 'danger';
      default:  return 'success';
    }
  }

  /** Tooltip/assistive hint for the status badge. */
  statusHelp(code?: string | number): string {
    switch (String(code)) {
      case '0': return 'El CUE se está generando.';
      case '1': return 'El CUE está disponible para envío.';
      case '2': return 'No se pudo generar el CUE.';
      default:  return '';
    }
  }

  /** Summary text for message states. */
  messageSummary(state: 'success' | 'info' | 'warning' | 'error'): string {
    switch (state) {
      case 'success': return 'OK';
      case 'info':    return 'Información';
      case 'warning': return 'Aviso';
      case 'error':   return 'Error';
      default:        return '';
    }
  }


  /* ─────────────────────────────── CUE ──────────────────────────────── */

  /** Fetch CUE status/history for the selected finca. */
  checkStatusCUE(data: FincasModel) {
    this.http
      .post<any[]>(`${environment.serverUrl}ws/index.php?p1=fincas&p2=checkCUE`,
        { id_finca: data.id, id_usuario: StorageManager.getUser().id }).pipe(catchError(error => {
          console.log('catch en checkCUE: ' + error);
          return of([]);
        }))
      .subscribe(async (res: any) => {

        const response = await res.data;

        if (res.statusCode === 200) {
          if (res.data['history'].length > 0) {

            this.statusCue = response['history'].at(0);
            this.cueHistory = response['history'];
            
            this.counter = {
              'fito': response['task_fito'],
              'ferti': response['task_ferti']
            };

          } 
        }
      });
  }

  /** Convenience wrapper for templates. */
  getStatusCUE(data: FincasModel): void {
    this.checkStatusCUE(data);
  }

  /**
   * Submit the CUE for a finca and monitor the processing status.
   * Handles 204/409/500 and the custom payload results ("FALLO"/"OK").
   */
  exportCUE(data: FincasModel): void {
    
    this.messageState = '';

    const hasRequired = (data.id_cliente != null) && (data.codigo_REA != null) && 
    (data.nif != null) && (data.comunidad_autonoma_INE != null);

    if(data.email !== 'info@locatec.es') {
      if (!hasRequired) {
        this.setMessage('error', {
          statusCode: 0,
          statusMessage: 'Falta datos para enviar CUE',
          data: [{
            body: 'Comprueba los datos del formulario y vuelve a intentarlo. Datos obligatorios: Comunidad autónoma, NIF, Código REA',
          }],
        });
        return;
      }
    }


    const url = `${environment.serverUrl}ws/index.php?p1=fincas&p2=sendCUE`;
    const baseParams = new HttpParams()
      .set('id_finca', data.id)
      .set('id_usuario', StorageManager.getUser().id)
      .set('comunidad_autonoma_ine', data.comunidad_autonoma_INE ?? '')
      .set('nif', data.nif ?? '')
      .set('codigo_REA', data.codigo_REA ?? '');

    this.http
      .get(url, { params: baseParams, observe: 'response' })
      .pipe(
        tap((response) => {
          if (response.status === HttpStatusCode.NoContent) {
            this.setMessage('success', {
              statusCode: response.status,
              statusMessage: 'No Content',
              data: [{ body: 'La finca no tiene los datos suficientes para enviar el CUE.' }],
            });
          }
        }),
        catchError((error) => {
          if (error.status === HttpStatusCode.Conflict) {
            
            this.showSpinner = false;

            this.setMessage('error', {
              statusCode: HttpStatusCode.Conflict,
              statusMessage: 'Conflict',
              data: [{ body: error.error?.data?.descripcion + '. Contacta con el administrador.' }],
            });
            return of();
          }

          if (error.status === HttpStatusCode.UnprocessableEntity) {
            this.showSpinner = false;

            this.setMessage('error', {
              statusCode: HttpStatusCode.UnprocessableEntity,
              statusMessage: 'Unprocessable Entity',
              data: [{ body: error.error?.statusMessage}],
            });
            return of();
          }

          if (error.status === HttpStatusCode.InternalServerError) {
            this.showSpinner = false;

            this.setMessage('error', {
              statusCode: HttpStatusCode.InternalServerError,
              statusMessage: 'Servicio no disponible. Contacta con el administrador.',
              data: [{ body: error.error?.data?.descripcion + '. Contacta con el administrador.' }],
            });
            return of();
          }

          return throwError(() => error);
        })
      )
      .subscribe({
        next: async (res: any) => {
          const resultado = res.body.resultado;

          if (resultado.codigoResultado === 'FALLO') {
            this.setMessage('success', {
              statusCode: 200,
              statusMessage: 'OK',
              data: [{ body: resultado.detalleImportacion }],
            });
            console.error('💥 Fallo en la formación del json => ', resultado.detalleImportacion);
            return;
          }

          if (resultado.codigoResultado === 'OK') {
            this.setMessage('success', {
              statusCode: 200,
              statusMessage: 'El CUE se ha enviado correctamente.',
              data: [{ body: 'El CUE se ha enviado correctamente.' }],
            });

            const code = resultado.detalleImportacion[0]?.codigo;

            // Simple polling until the API signals completion.
            const POLL_EVERY_MS = 8000;
            const CHECK_PARAMS = new HttpParams().set('codigo', code);

            while (true) {
              await new Promise((resolve) => setTimeout(resolve, POLL_EVERY_MS));
              const loopResponse = await firstValueFrom<any>(this.http.get(url, { params: CHECK_PARAMS }));

              if(loopResponse?.resultado?.codigoResultado === 'FALLO'){
                this.setMessage('error', {
                  statusCode: HttpStatusCode.InternalServerError,
                  statusMessage: 'Fallo en la verificación del CUE:  Contiene ' + 
                  loopResponse?.resultado?.detalleImportacion.length + ' errores.',
                });
                break;
              }
              
              if (Boolean(loopResponse?.someCondition)) { break; }
            }
          }

          this.checkStatusCUE(data);
        },
      });
  }

  /* ───────────────────────── BaseView overrides ─────────────────────── */

  /** Open a modal and prepare form/state for the given data. */
  public override show(modal: string, data: any): void {
    const formType = modal === 'delete' ? +FormType.DELETION : +FormType.INFORMATIVE;
    
    this.form = new FincasFormDefinition(formType, data as Record<string, string>, null);

    this.model = data;
    this.canShow[modal] = true;

    if (modal === 'sendCUE') { this.checkStatusCUE(data); }

    // Close any other open modal.
    for (const key in this.canShow) {
      if (key !== modal && (this.canShow[key] ?? false)) { this.canShow[key] = false; }
    }
  }

  public override hide(modal: string): void {
    this.model = {} as any;
    this.canShow[modal] = false;
    this._deleteRequest.reset();
  }


  public add(): void {
    if (!(Boolean(StorageManager.getClient())) && this.env.userCanSeeClientes) {
      this.dashboard.openClientDialog();
    } else {
      this.router.navigate(['dashboard', 'crear-finca']);
    }
  }

  public edit(data: FincasModel): void {
    this.router.navigate(['dashboard', 'editar-finca', data.id, data]);
  }

  public duplicate(data: FincasModel): void {
    this.router.navigate(['dashboard', 'duplicar-finca', data.id]);
  }

  public override deleteRegister(): void {
    if (!this.model.id) {
      this.deleteRequest.hasError = true;
      throw new Error('Trying to DELETE a register without primary key');
    }

    if (!this.deleteWarning) {
      this.deleteWarningMessage = 'Se borrará toda la información asociada';
      this.deleteWarning = `¡${this.env.fincaName} con datos! ¿Continuar?`;
      return;
    }

    this.deleteWarning = '';
    this.deleteWarningMessage = '';
    this.deleteRequest.safePerform(this.model);
    this.deleteRequest.response(() => {
      if (!(this.deleteRequest.hasError ?? false)) { this.getRequest.safePerform(); }
    });
  }

  /* ────────────────────────────── UI utils ───────────────────────────── */

  /**
   * Map exploitation type code → human label (returns new object, non-mutating).
   */
  public changeTipoExplotacion(
    model: ItemInterface<{ tipo_explotacion?: string }>
  ): ItemInterface<{ tipo_explotacion?: string }> {
    const current = String(model['tipo_explotacion'] ?? '');
    
    if (!current) { return model; }

    const found = TIPOS.find((t) => current.localeCompare(t.value) === 0);
    
    return found ? { ...model, tipo_explotacion: found.label } : model;
  }

  /** Reset dependent selectors when sector changes. */
  public filterSectores(): void {
    this.form.lists.sectores.selected = null;
    this.form.lists.parcelas.selected = null;
    this.form.lists.variedades.selected = null;
  }

  /** Deduplicate parcela options: same label + finca + sector. */
  public filterVariedades(): void {
    const seen = new Set<string>();
    
    this.form.lists.parcelas.values = this.form.lists.parcelas.values
      .filter((it) => {
        if (!(Boolean((it?.value)))) { return false; }
        
        const key = `${it.label}_${it.value.id_finca}_${it.value.id_sector}`;
        
        if (seen.has(key)) { return false; }
        seen.add(key);
        return true;
      })
      .map((it) => it);
  }

  /** Pass-through content transform (hook point for future rules). */
  public globalVisualTransform(value: string): string {
    return value;
  }

  /** Per-cell style rules (thresholds & numeric alignment). */
  public globalConditionalStyle(value: string | number, col: ColInterface): ColStylesInterface {
    if (col.field === 'lectura_contador' && +value >= DEFAULTS.LECTURA_CONTADOR_ALERT_THRESHOLD) {
      return { textAlign: 'center', marginRight: '60px', color: 'red' };
    }
    if (Utils.isNumber(value?.toString() ?? '') || col.field === 'telefono') {
      return { textAlign: 'center', marginRight: '60px' };
    }
    if (col.field === 'provincia' || col.field === 'titular_nombre') {
      return { textAlign: 'center' };
    }
    return {textAlign: 'center'};
  }

  /* ─-─────────────────────────── PAC upload ─────────────────────────── */

  /** Basic client-side file validation for the PAC Excel. */
  checkFile(event: FileSelectEvent): void {
    this.showPACError = false;
    const file = event.files?.[0]?.name ?? '';

    if (!file.endsWith('.xlsx') && !file.endsWith('.xls')) {
      this.showPACError = true;
      this.txtPACError = 'El archivo debe ser un Excel(xlsx)';
    }
  }

  /** Open upload dialog and compose the upload URL with required params. */
  uploadDialog(data: FincasModel): void {
    this.showModalPAC = true;
    this.fincaPAC = data.id;

    const base = `${environment.serverUrl}ws/informes/excel_pac/fileUploadData.php`;
    const qs = new URLSearchParams();

    qs.set('id_finca', String(data.id));

    if (data.id_cliente != null) { qs.set('id_cliente', String(data.id_cliente)); }

    const userId = data.id_usuario ?? this.env.user.id;
    
    if (userId != null) { qs.set('id_usuario', String(userId)); }

    this.urlUpload = `${base}?${qs.toString()}`;
  }

  /** Handle a completed upload and render the summary in the dialog. */
  uploaded(event: FileUploadEvent): void {
    if (!(event.originalEvent instanceof HttpResponse)) { return; }

    const response = event.originalEvent.body as UploadResponse;
    const parcelasFound = response.content_found;
    const parcelasNotFound = response.content_not_found;
    const especiesNotFound = response.especie_not_found;

    const arrayEspeciesNotFound: number[] = this.unique(especiesNotFound);
    const arrayParcelasFound: string[] = parcelasFound.map((it: ParcelasModel) => it.numero ?? '');
    const arrayParcelasNotFound: string[] = parcelasNotFound.map((it: ParcelasModel) => it.numero ?? '');

    this.showPACError = true;
    this.txtPACInfo =
      'Carga de las Parcelas realizada correctamente<br />' +
      arrayParcelasNotFound.join(', ') +
      '<br>Parcelas actualizadas<br />' +
      arrayParcelasFound.join(', ') +
      '<br/><br/>';

    if (arrayEspeciesNotFound.length === 0) {
      this.txtPACInfo += '¡Todos los Especies/Cultivos han sido encontrados! <br />';
    } else {
      this.txtPACError = '¡Especies/Cultivos no encontrados! <br />' + arrayEspeciesNotFound.join(', ');
    }
  }

  /** Close PAC dialog and refresh the list (no full page reload). */
  closeFUDialog(): void {
    this.showModalPAC = false;
    this.showPACError = false;
    this.getRequest.safePerform();
  }

  /** Return unique numbers while preserving order. */
  unique(array: number[]): number[] {
    return Array.from(new Set(array));
  }

  /* ─────────────────────────── Cuaderno (reports) ───────────────────── */

  /** Build the year dropdown from the domain start year up to the current year. */
  public generateYearList(): void {
    const years: { label: string | number; value: number | null }[] = [
      { label: 'Seleccione un año', value: null },
    ];

    for (let y = DEFAULTS.FIRST_CUADERNO_YEAR; y <= this.currentYear; y++) {
      years.push({ label: y, value: y });
    }
    this.form.lists.year.filtered = years as any;
  }

  /**
   * Verify and generate GML for a parcela.
   * Shows an alert if the backend verification fails.
   */
  public generaGML(data: { id: string }): void {
    firstValueFrom(this.fincasSvc.verificaGML(data.id))
      .then((resp: { success: string; mensaje: string }) => {
        if (resp.success) {
          this.fincasSvc.generaGML(data.id);
        } else {
          alert(resp.mensaje);
        }
      })
      .catch(error => { console.log('error', error); });
  }

  /** Generate a CSV from the current filter and trigger a client-side download. */
  public downloadCsv(): void {
    this.form = new FincasFormDefinition(+FormType.EXPORT, this.filtering.value as any, null);
    const csvContent = createCsv(this.form.fieldNames, this.form.fieldLabels, this.filtering.value as FincasModel[]);
    const csvLink = document.getElementById('csvLink') as HTMLAnchorElement;

    csvLink.download = `${this.form.formFields.name}.csv`;
    csvLink.href = encodeURI(csvContent);
    csvLink.click();
  }

  /* ───────────────────────────── Helpers ─────────────────────────────── */

  /** Centralized helper to update UI message state and payload. */
  private setMessage(state: MessageState, payload?: any): void {
    this.messageState = state;
    this.importResult = payload ?? null;
    this.showSpinner = false;
  }
}
